import { clamp01, hash2, smoothstep } from "@bfly/engine/math";
import type { PortraitOutline } from "../portrait/face-outline.js";
import { dotOpacity, halftoneDot } from "./halftone-dissolve.js";

// The about pages' pictures dissolve into halftone dots under the face. Each dot goes when the
// face's outline above its column reaches it, the same way each word does (text-effects.ts), so
// the dissolve's edge has the shape of the face and the butterfly as it sweeps down the picture.
//
//   outline (the face's bottom edge, column by column)
//   ‾‾‾\__/‾‾‾‾\___
//   . . . . . . . .   gone: at the outline and under it
//   o o o o o O O O   dissolving: in the `DOT_LEAD` pixels before the outline reaches them
//   O O O O O O O O   whole: the rest of each column is one rectangle
//
// A canvas over the picture, in its section, draws the picture through those dots (as a clip
// path), and the real picture hides. The canvas scrolls with the page, so it can't lag behind.
// Nothing reads pixels back, so a video frame from the CDN (another origin) works fine.

/** The dot grid's spacing, in CSS pixels. */
const DOT_CELL = 8;
/** A dot starts this many pixels before the outline reaches it, so there's a band of
    dissolving dots between the face and the whole picture. It's 8 rows: short enough that a
    video player (which locks once its picture starts to dissolve) locks late. */
export const DOT_LEAD = 8 * DOT_CELL;
/** ...and is gone this many pixels after the outline reaches it: right away, so nothing is
    still dissolving under the face. */
export const DOT_TAIL = 0;
/** Each dot reaches up to this fraction further than `DOT_LEAD`, by noise from its position,
    so the dissolve's edge is ragged instead of ruler-straight. */
const DOT_SPREAD = 0.5;
/** How far below the stopped portrait a keyboard focus puts a player's video: past the
    furthest dot's reach, plus 16px, so its picture stays whole and its controls still take
    keys. The player scrolls by this much (jrsm-video.ts), and where the viewport can't fit
    it, home.ts lets the portrait scroll with the page. */
export const FOCUS_CLEARANCE = DOT_LEAD * (1 + DOT_SPREAD) + 16;
/** The furthest that any dot starts before the outline. */
export const DOT_REACH_MAX = DOT_LEAD * (1 + DOT_SPREAD);
// A dissolving dot drifts up to this far (in CSS pixels) from its grid point, and its size
// varies by up to this fraction, both by noise from its position, so the dots don't line up in
// columns. Both start at nothing when the dot starts and reach full by the middle of its
// dissolve, so there are no gaps along the edge with the whole picture.
const DOT_JITTER = 2.5;
const DOT_SIZE_JITTER = 0.2;
// Each dot reads the outline up to this far (in CSS pixels) to either side, picked by smooth
// noise from its position (the scale is in pixels too), so the edge wobbles a little. Neighbors
// read nearby spots, so the edge doesn't speckle. It's kept small so the edge stays close to
// the face's own arch (face-outline.ts): a wider read made a wall next to the steep parts of
// the face, where the edge should arch.
const OUTLINE_JITTER = 6;
const OUTLINE_JITTER_SCALE = 24;

// The dots' opacity comes in this many steps (one erase per step, not per dot).
const OPACITY_STEPS = 16;

/** Smooth noise for a spot on the page, 0 to 1: `hash2` at the corners of a grid of `scale`
    pixels, eased in between, so nearby spots get similar values. */
function smoothNoise(x: number, y: number, scale: number): number {
  const gx = x / scale;
  const gy = y / scale;
  const ix = Math.floor(gx);
  const iy = Math.floor(gy);
  const tx = smoothstep(0, 1, gx - ix);
  const ty = smoothstep(0, 1, gy - iy);
  const corner = (cx: number, cy: number): number => hash2(cx * 7 + 1, cy * 13 + 3);
  const top = corner(ix, iy) + (corner(ix + 1, iy) - corner(ix, iy)) * tx;
  const bottom = corner(ix, iy + 1) + (corner(ix + 1, iy + 1) - corner(ix, iy + 1)) * tx;
  return top + (bottom - top) * ty;
}

/** How far ahead of the outline the dot at page (`x`, `y`) starts: `DOT_LEAD` up to
    `DOT_REACH_MAX`. */
function dotReach(x: number, y: number): number {
  return DOT_LEAD * (1 + DOT_SPREAD * hash2(x, y));
}

/** The outline's value at viewport x `x`. `values` holds one value per column, each `step`
    pixels wide, starting at `start`. Between two columns' middles the value slopes, so a jump
    between columns doesn't make a hard edge. Null past either end of the outline. */
export function outlineAt(
  values: Float32Array,
  start: number,
  step: number,
  x: number,
): number | null {
  const at = (x - start) / step;
  if (at < 0 || at >= values.length) return null;
  const middle = at - 0.5;
  const i = Math.floor(middle);
  const low = values[Math.max(0, i)] ?? 0;
  const high = values[Math.min(values.length - 1, i + 1)] ?? low;
  return low + (high - low) * (middle - i);
}

// The lowest point the outline reaches between viewport x `from` and `to`, or null if it has
// no value there.
function outlineDeepest(
  values: Float32Array,
  start: number,
  step: number,
  from: number,
  to: number,
): number | null {
  const first = Math.max(0, Math.floor((from - start) / step));
  const last = Math.min(values.length - 1, Math.floor((to - start) / step));
  let deepest: number | null = null;
  for (let i = first; i <= last; i++)
    deepest = Math.max(deepest ?? -Infinity, values[i] ?? -Infinity);
  return deepest;
}

/** A dot's progress, 0 to 1. The dot is at page y `dotY` and starts `reach` pixels ahead of
    the outline; its column's outline rests at page y `rest` and is now at viewport y `now`.
    Like `dissolveProgress` (text-effects.ts), nothing at or above the resting outline
    dissolves, so nothing dissolves before the portrait stops. */
export function dotProgress(
  dotY: number,
  rest: number,
  now: number,
  scrollY: number,
  reach = DOT_LEAD,
): number {
  if (dotY <= rest) return 0;
  const lead = Math.min(reach, dotY - rest);
  const gap = dotY - scrollY - now;
  return clamp01((lead - gap) / (lead + DOT_TAIL));
}

/** Where `object-fit: cover` with `object-position` (`x`, `y`, as fractions) puts an image
    with aspect `width` : `height` in a `boxWidth` x `boxHeight` box. The whole image is drawn
    there, and the box crops it. Only the aspect ratio matters, not the image's units, which is
    handy: with `srcset`, `naturalWidth` is corrected for the screen's density, but
    `drawImage()`'s source rectangle is in the file's pixels. */
export function coverTarget(
  width: number,
  height: number,
  boxWidth: number,
  boxHeight: number,
  x: number,
  y: number,
): { x: number; y: number; w: number; h: number } {
  const scale = Math.max(boxWidth / width, boxHeight / height);
  const w = width * scale;
  const h = height * scale;
  return { x: (boxWidth - w) * x, y: (boxHeight - h) * y, w, h };
}

// The `data-halftone` mark on a picture that isn't whole.
type PictureMark = "dissolving" | "gone";

// A picture in the about sections: an image, or a player's video (or its poster, before it
// has a frame). `marked` is the element that gets `data-halftone`: the image, or the player,
// which pauses its video while it dissolves (elements/jrsm-video.ts).
interface Picture {
  marked: HTMLElement;
  media: HTMLImageElement | HTMLVideoElement;
  poster: HTMLImageElement | null;
  section: HTMLElement;
  // The picture's box in page pixels, and its offset within its section.
  left: number;
  top: number;
  width: number;
  height: number;
  offsetLeft: number;
  offsetTop: number;
  // `object-position`, as fractions.
  positionX: number;
  positionY: number;
  canvas: HTMLCanvasElement | null;
  // The last mark written, or null while the picture is whole.
  mark: PictureMark | null;
}

// A fraction from one part of `object-position` ("35%"). Anything else counts as the middle.
function share(part: string | undefined): number {
  return part?.endsWith("%") ? Number.parseFloat(part) / 100 : 0.5;
}

export class HalftoneLayer {
  private pictures: Picture[] = [];
  // The outline and scroll from the last update, so a picture can redraw when its pixels
  // arrive.
  private last: { outline: PortraitOutline; scrollX: number; scrollY: number } | null = null;
  // The media this layer watches for pixels. A dissolving picture redraws when its image loads
  // or its video gets a frame (otherwise it only draws when the outline changes).
  private readonly watched = new WeakSet<HTMLImageElement | HTMLVideoElement>();

  constructor(private readonly root: HTMLElement) {}

  /** Finds the pictures and measures their boxes (after a layout change). A picture keeps its
      canvas and its mark, so a measure (every bio rewrite, every theme change) doesn't make
      new canvases. */
  measure(): void {
    const previous = new Map(this.pictures.map((picture) => [picture.media, picture]));
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const found: Picture[] = [];
    for (const section of this.root.querySelectorAll(".about")) {
      if (!(section instanceof HTMLElement)) continue;
      const origin = section.getBoundingClientRect();
      const add = (marked: HTMLElement, media: HTMLImageElement | HTMLVideoElement): void => {
        const box = media.getBoundingClientRect();
        if (box.width === 0 || box.height === 0) return;
        const [x, y] = getComputedStyle(media).objectPosition.split(" ");
        const old = previous.get(media);
        previous.delete(media);
        this.watch(media);
        found.push({
          marked,
          media,
          poster: old?.poster ?? null,
          section,
          left: box.left + scrollX,
          top: box.top + scrollY,
          width: box.width,
          height: box.height,
          offsetLeft: box.left - origin.left,
          offsetTop: box.top - origin.top,
          positionX: share(x),
          positionY: share(y),
          canvas: old?.canvas ?? null,
          mark: old?.mark ?? null,
        });
      };
      for (const image of section.querySelectorAll("img")) add(image, image);
      for (const player of section.querySelectorAll("jrsm-video")) {
        const video = player.querySelector("video");
        if (video) add(player, video);
      }
    }
    // A picture that left the page shows again; the rest move their canvases to their new
    // boxes.
    for (const gone of previous.values()) this.show(gone);
    this.pictures = found;
    for (const picture of this.pictures) if (picture.canvas) this.place(picture, picture.canvas);
  }

  /** Shows every picture whole, with no canvas (for reduced motion, or when there's no
      outline). */
  clear(): void {
    for (const picture of this.pictures) this.show(picture);
  }

  /** Draws each picture for where the outline is now. */
  update(outline: PortraitOutline | null, scrollX: number, scrollY: number): void {
    if (!outline) {
      this.last = null;
      this.clear();
      return;
    }
    this.last = { outline, scrollX, scrollY };
    // If a picture's top is further below the outline's lowest point than any dot reaches, no
    // dot has started: it stays whole, for free.
    let lowest = Number.NEGATIVE_INFINITY;
    for (const bottom of outline.bottoms) lowest = Math.max(lowest, bottom);
    for (const picture of this.pictures) {
      if (picture.top - scrollY - lowest > DOT_REACH_MAX) this.show(picture);
      else if (picture.top + picture.height - scrollY <= 0) this.release(picture);
      else this.draw(picture, outline, scrollX, scrollY);
    }
  }

  // A picture above the viewport, under the face or past it. It stays hidden (a player stays
  // locked) but drops its canvas, so no canvas hangs around or redraws off screen. A picture
  // wider than the face keeps some whole columns, so it never gets to "gone" on its own.
  private release(picture: Picture): void {
    picture.canvas?.remove();
    picture.canvas = null;
    this.mark(picture, "gone");
  }

  private show(picture: Picture): void {
    if (!picture.canvas && picture.mark === null) return;
    picture.canvas?.remove();
    picture.canvas = null;
    this.mark(picture, null);
  }

  // Only touches the DOM when the mark changes.
  private mark(picture: Picture, mark: PictureMark | null): void {
    if (mark === picture.mark) return;
    picture.mark = mark;
    if (mark) picture.marked.setAttribute("data-halftone", mark);
    else picture.marked.removeAttribute("data-halftone");
  }

  private draw(picture: Picture, outline: PortraitOutline, scrollX: number, scrollY: number): void {
    const { left, top, width, height } = picture;
    // The started dots, as a clip path. In each column:
    // - gone dots are left out
    // - dissolving dots are circles
    // - a whole dot among them is a full circle (it covers its cell, and its edge next to a
    //   gone dot is round, not square)
    // - everything past any dot's reach is one rectangle
    const path = new Path2D();
    // The dissolving dots, grouped by opacity step. Each step's dots get erased by its
    // fraction.
    const fades: (Path2D | undefined)[] = [];
    let started = false;
    let whole = false;
    const { bottoms, restBottoms, step: outlineStep } = outline;
    for (let x = DOT_CELL / 2; x < width; x += DOT_CELL) {
      const viewX = left + x - scrollX;
      // The lowest the outline gets where this column's dots read it. Below that, plus a dot's
      // reach, none of the column's dots have started.
      const deepest = outlineDeepest(
        bottoms,
        outline.left,
        outlineStep,
        viewX - OUTLINE_JITTER,
        viewX + OUTLINE_JITTER,
      );
      if (deepest === null) {
        // No face above this column: it never dissolves.
        path.rect(x - DOT_CELL / 2, 0, DOT_CELL, height);
        whole = true;
        continue;
      }
      for (let y = DOT_CELL / 2; y < height; y += DOT_CELL) {
        if (top + y - scrollY - deepest > DOT_REACH_MAX) {
          path.rect(x - DOT_CELL / 2, y - DOT_CELL / 2, DOT_CELL, height - (y - DOT_CELL / 2));
          whole = true;
          break;
        }
        const placeX = left + x;
        const placeY = top + y;
        const readX =
          viewX + (smoothNoise(placeX, placeY, OUTLINE_JITTER_SCALE) - 0.5) * 2 * OUTLINE_JITTER;
        const now = outlineAt(bottoms, outline.left, outlineStep, readX);
        const rest = outlineAt(restBottoms, outline.left, outlineStep, readX);
        const progress =
          now === null || rest === null
            ? 0
            : dotProgress(placeY, rest, now, scrollY, dotReach(placeX, placeY));
        if (progress === 0) {
          // A full dot reaches the cell's corners (`halftoneDot(0)` is the square root of 2).
          const radius = (Math.SQRT2 * DOT_CELL) / 2;
          path.moveTo(x + radius, y);
          path.arc(x, y, radius, 0, 2 * Math.PI);
          whole = true;
          continue;
        }
        started = true;
        if (progress < 1) {
          const loose = Math.min(1, progress * 2);
          const cx = x + (hash2(placeX + 7919, placeY) - 0.5) * 2 * DOT_JITTER * loose;
          const cy = y + (hash2(placeX, placeY + 7919) - 0.5) * 2 * DOT_JITTER * loose;
          const size =
            1 + (hash2(placeX + 104729, placeY + 104729) - 0.5) * 2 * DOT_SIZE_JITTER * loose;
          const radius = (halftoneDot(progress) * size * DOT_CELL) / 2;
          path.moveTo(cx + radius, cy);
          path.arc(cx, cy, radius, 0, 2 * Math.PI);
          whole = true;
          const step = Math.round((1 - dotOpacity(progress)) * OPACITY_STEPS);
          if (step > 0) {
            const fade = (fades[step] ??= new Path2D());
            fade.moveTo(cx + radius, cy);
            fade.arc(cx, cy, radius, 0, 2 * Math.PI);
          }
        }
      }
    }
    if (!started) {
      this.show(picture);
      return;
    }
    this.mark(picture, whole ? "dissolving" : "gone");
    if (!whole) {
      picture.canvas?.remove();
      picture.canvas = null;
      return;
    }
    const canvas = picture.canvas ?? this.canvasFor(picture);
    // Placed again every draw: the device's resolution changes without a resize when the
    // window moves to another screen.
    this.place(picture, canvas);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.clip(path);
    this.drawPicture(ctx, picture);
    ctx.restore();
    // Each dissolving dot fades as it shrinks, like the text does: erase its fraction.
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    fades.forEach((fade, step) => {
      if (!fade) return;
      ctx.globalAlpha = step / OPACITY_STEPS;
      ctx.fill(fade);
    });
    ctx.restore();
  }

  private drawPicture(ctx: CanvasRenderingContext2D, picture: Picture): void {
    const { media, width, height } = picture;
    if (media instanceof HTMLVideoElement) {
      // A video with no frame yet (`preload="none"`) shows its poster.
      const framed = media.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
      if (framed) ctx.drawImage(media, 0, 0, width, height);
      else {
        // A poster that failed to load is "complete" with no pixels, and drawing it throws.
        const poster = this.posterOf(picture);
        if (poster?.complete && poster.naturalWidth > 0) ctx.drawImage(poster, 0, 0, width, height);
      }
      return;
    }
    if (!media.complete || media.naturalWidth === 0) return;
    const target = coverTarget(
      media.naturalWidth,
      media.naturalHeight,
      width,
      height,
      picture.positionX,
      picture.positionY,
    );
    ctx.drawImage(media, target.x, target.y, target.w, target.h);
  }

  // A video's poster as an image to draw, made the first time a dissolve needs it. The video's
  // own poster loads lazily (`loading="lazy"`), and making the image at measure time would load
  // every poster with the page. By the time a dissolve needs it, the video is near the
  // viewport, so the poster is loaded or on its way, and the picture redraws when it arrives.
  private posterOf(picture: Picture): HTMLImageElement | null {
    const { media } = picture;
    if (!(media instanceof HTMLVideoElement) || !media.poster) return null;
    if (picture.poster?.src !== media.poster) {
      const poster = new Image();
      poster.addEventListener("load", () => this.redraw(media), { once: true });
      poster.src = media.poster;
      picture.poster = poster;
    }
    return picture.poster;
  }

  private watch(media: HTMLImageElement | HTMLVideoElement): void {
    if (this.watched.has(media)) return;
    this.watched.add(media);
    const redraw = (): void => this.redraw(media);
    if (media instanceof HTMLVideoElement)
      for (const type of ["loadeddata", "seeked"]) media.addEventListener(type, redraw);
    else media.addEventListener("load", redraw);
  }

  // Redraws one picture with the last outline, if it's dissolving.
  private redraw(media: HTMLImageElement | HTMLVideoElement): void {
    const picture = this.pictures.find((each) => each.media === media);
    if (!picture || !this.last || picture.mark !== "dissolving") return;
    const { outline, scrollX, scrollY } = this.last;
    this.draw(picture, outline, scrollX, scrollY);
  }

  // A canvas over the picture, in its section.
  private canvasFor(picture: Picture): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.className = "halftone";
    // These canvases come and go, so the text effects ignore them.
    canvas.dataset.fx = "none";
    canvas.setAttribute("aria-hidden", "true");
    this.place(picture, canvas);
    picture.section.append(canvas);
    picture.canvas = canvas;
    return canvas;
  }

  // Puts the canvas over its picture's box, at the device's resolution. The backing store only
  // gets reallocated when its size changes.
  private place(picture: Picture, canvas: HTMLCanvasElement): void {
    const dpr = window.devicePixelRatio || 1;
    const width = Math.round(picture.width * dpr);
    const height = Math.round(picture.height * dpr);
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    canvas.style.setProperty("left", `${picture.offsetLeft}px`);
    canvas.style.setProperty("top", `${picture.offsetTop}px`);
    canvas.style.setProperty("width", `${picture.width}px`);
    canvas.style.setProperty("height", `${picture.height}px`);
  }
}
